import { useCallback, useEffect, useRef, useState } from 'react';

import { tokenService } from '../services/tokenService';

export type AdminSupportEventType =
  | 'support_ticket_created'
  | 'support_ticket_user_message'
  | 'ping';

export interface AdminSupportAlertData {
  ticket_id: string;
  user_id?: string;
  creator_name?: string | null;
  creator_email?: string | null;
  status?: string;
  priority?: string;
  category?: string | null;
  preview?: string | null;
  created_at?: string;
  updated_at?: string;
  message_id?: string;
  message_created_at?: string;
}

export interface AdminSupportWSEvent {
  type: AdminSupportEventType;
  data?: AdminSupportAlertData;
}

const WS_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(
  /^http/,
  'ws'
);

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const AUTH_CLOSE_CODES = new Set([4001, 1008, 1006]);

export function useAdminSupportWebSocket(
  enabled: boolean,
  onEvent: (event: AdminSupportWSEvent) => void
): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const closeWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      closeWs();

      return undefined;
    }

    let alive = true;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function scheduleReconnect() {
      attempt += 1;
      const delay = Math.min(
        RECONNECT_BASE_MS * Math.pow(2, attempt),
        RECONNECT_MAX_MS
      );
      reconnectTimer = setTimeout(connect, delay);
    }

    function connect(): void {
      if (!alive) return;

      const token = tokenService.getAccessToken();
      if (!token) return;

      closeWs();

      const url = `${WS_URL}/api/v1/support/tickets/staff/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);

      ws.onopen = () => {
        attempt = 0;
        setIsConnected(true);
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const parsed: AdminSupportWSEvent = JSON.parse(event.data);

          if (parsed.type === 'ping') return;
          onEventRef.current(parsed);
        } catch {
          // Ignore malformed messages.
        }
      };

      ws.onclose = (event: CloseEvent) => {
        wsRef.current = null;
        setIsConnected(false);

        if (!alive) return;

        if (AUTH_CLOSE_CODES.has(event.code)) {
          tokenService
            .refreshToken()
            .then(() => {
              if (alive) connect();
            })
            .catch(() => {
              alive = false;
            });
          return;
        }

        scheduleReconnect();
      };

      ws.onerror = () => {
        // onclose handles reconnects.
      };

      wsRef.current = ws;
    }

    connect();

    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      closeWs();
    };
  }, [enabled, closeWs]);

  return { isConnected };
}
